import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Box,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { get, del } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";
import DeleteModal from "../../components/Custom/DeleteModal/DeleteModal";

const JournalResponses = () => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalResponses, setTotalResponses] = useState(0);
  const [search, setSearch] = useState("");
  const [viewModal, setViewModal] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteResponse, setDeleteResponse] = useState(null);

  const fetchResponses = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        limit: rowsPerPage,
        search,
      });

      const response = await get(`/api/dashboard/journal/responses?${queryParams}`);
      console.log("Journal Responses API Response:", response);
      
      // Check if response is an error object (from axios error handler)
      if (response?.errorType || response?.response === "Failure") {
        console.error("API Error:", response);
        const errorMessage = typeof response?.message === 'string' 
          ? response.message 
          : "Failed to fetch journal responses";
        toastMessage(errorMessage, "error");
        setResponses([]);
        setTotalResponses(0);
        return;
      }
      
      // Check if response has success property (normal API response)
      if (response && typeof response === 'object' && response.success === true) {
        const responsesData = response.data?.responses || [];
        const totalCount = response.data?.pagination?.totalResponses || 0;
        
        setResponses(Array.isArray(responsesData) ? responsesData : []);
        setTotalResponses(typeof totalCount === 'number' ? totalCount : 0);
        
        // Log if no responses found
        if (!responsesData || responsesData.length === 0) {
          console.log("No journal responses found in database");
        }
      } else {
        console.error("API returned unexpected response:", response);
        const errorMessage = typeof response?.message === 'string' 
          ? response.message 
          : "Failed to fetch journal responses";
        toastMessage(errorMessage, "error");
        setResponses([]);
        setTotalResponses(0);
      }
    } catch (error) {
      console.error("Error fetching journal responses:", error);
      console.error("Error details:", error.response?.data);
      toastMessage(
        error.response?.data?.message || "Failed to fetch journal responses", 
        "error"
      );
      setResponses([]);
      setTotalResponses(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchResponses();
  }, [page, rowsPerPage, search]);

  const handleViewResponse = (response) => {
    setSelectedResponse(response);
    setViewModal(true);
  };

  const handleDeleteResponse = async () => {
    try {
      const response = await del(`/api/dashboard/journal/responses/${deleteResponse._id}`);
      if (response?.success) {
        toastMessage("Journal response deleted successfully", "success");
        setDeleteModalOpen(false);
        setDeleteResponse(null);
        fetchResponses();
      }
    } catch (error) {
      toastMessage(error.response?.data?.message || "Something went wrong", "error");
    }
  };

  const openDeleteModal = (response) => {
    setDeleteResponse(response);
    setDeleteModalOpen(true);
  };

  const getResponseTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "text":
        return "primary";
      case "rating":
        return "success";
      case "multiple_choice":
        return "warning";
      default:
        return "default";
    }
  };

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const renderResponseValue = (response) => {
    if (response.responseType === "RATING") {
      return `Rating: ${response.ratingValue}/5`;
    } else if (response.responseType === "MULTIPLE_CHOICE") {
      return response.response;
    } else {
        return truncateText(response.response, 100);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  return (
    <Layout>
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            Journal Responses
          </Typography>
          
          {/* Search Bar */}
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <TextField
              label="Search responses"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={fetchResponses}
            >
              Search
            </Button>
          </Box>

          {/* Responses Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Question</TableCell>
                  <TableCell>Response</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography>Loading...</Typography>
                    </TableCell>
                  </TableRow>
                ) : responses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography>No journal responses found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  responses.map((response) => (
                  <TableRow key={response._id}>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {response.userId?.profilePicture ? (
                            <img
                              src={response.userId.profilePicture}
                              alt="Profile"
                              style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                          ) : (
                            <PersonIcon />
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {response.userId?.fullname || "Unknown User"}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {response.userId?.email || "No email"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {response.categoryId?.title || "Unknown Category"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Session {response.categoryId?.sessionNo || "N/A"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {truncateText(response.questionId?.title || response.questionId?.question, 60)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {renderResponseValue(response)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={response.responseType || "text"}
                        color={getResponseTypeColor(response.responseType)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(response.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View Response">
                          <IconButton
                            size="small"
                            onClick={() => handleViewResponse(response)}
                              color="info"
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Response">
                          <IconButton
                            size="small"
                              onClick={() => openDeleteModal(response)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            component="div"
            count={totalResponses}
            page={page - 1}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50]}
          />
          </CardContent>
        </Card>

        {/* View Response Modal */}
        <Dialog
          open={viewModal}
          onClose={() => setViewModal(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">
                Response Details
              </Typography>
              <IconButton onClick={() => setViewModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {selectedResponse && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                    {selectedResponse.userId?.profilePicture ? (
                      <Avatar 
                        src={selectedResponse.userId.profilePicture} 
                        sx={{ width: 48, height: 48 }}
                      />
                    ) : (
                      <Avatar sx={{ width: 48, height: 48, bgcolor: "#e0e0e0" }}>
                        {selectedResponse.userId?.fullname?.charAt(0)?.toUpperCase() || "U"}
                      </Avatar>
                    )}
                    <Box>
                      <Typography variant="h6">
                        {selectedResponse.userId?.fullname || "Unknown User"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedResponse.userId?.email || "No email"}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {selectedResponse.userId?.phone || "No phone"}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Category & Question
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Category:</strong> {selectedResponse.categoryId?.title || "Unknown Category"}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Question:</strong> {selectedResponse.questionId?.question || "Unknown Question"}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Question Type:</strong> {selectedResponse.questionId?.answerType || "Unknown"}
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Response
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {selectedResponse.response}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip
                      label={selectedResponse.responseType}
                      size="small"
                      sx={{
                        backgroundColor: selectedResponse.responseType === "RATING" ? "#e3f2fd" : 
                                       selectedResponse.responseType === "MULTIPLE_CHOICE" ? "#f3e5f5" : "#e8f5e8",
                        color: selectedResponse.responseType === "RATING" ? "#1976d2" : 
                               selectedResponse.responseType === "MULTIPLE_CHOICE" ? "#7b1fa2" : "#2e7d32"
                      }}
                    />
                    {selectedResponse.ratingValue && (
                      <Chip
                        label={`Rating: ${selectedResponse.ratingValue}/5`}
                        size="small"
                        color="primary"
                      />
                    )}
                  </Box>
                </Box>

                <Box>
                  <Typography variant="h6" gutterBottom>
                    Date & Time
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    <strong>Created:</strong> {new Date(selectedResponse.createdAt).toLocaleString()}
                  </Typography>
                  {selectedResponse.updatedAt !== selectedResponse.createdAt && (
                    <Typography variant="body2" color="textSecondary">
                      <strong>Updated:</strong> {new Date(selectedResponse.updatedAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewModal(false)}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Modal */}
        <DeleteModal
          open={deleteModalOpen}
          onClose={() => setDeleteModalOpen(false)}
          onDelete={handleDeleteResponse}
          data={deleteResponse}
        />
    </Layout>
  );
};

export default JournalResponses; 

